import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  approveRewardPlan, createRewardPlan, deleteRewardPlan, endBenefit, enrollBenefit, fillRewardPlan, loadBenefitPlans, loadDirectory,
  loadEnrollments, loadOrganisation, loadRewardLines, loadRewardPlans, saveBenefitPlan, setRewardLine,
  type BenefitPlan, type Enrollment, type RewardKey, type RewardLine, type RewardPlan,
} from "../data";
import { money } from "../lib/payslip";
import type { Department, DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const KEYS: RewardKey[] = ["bonus", "commission", "award", "profit_share"];

/** Bonuses and profit share (variable pay) and benefits, each shown when its module is on. */
export function Rewards({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const tabs: ["plans" | "benefits", StringKey][] = [];
  if (ws.enabled.has("variable_pay")) tabs.push(["plans", "rwTabPlans"]);
  if (ws.enabled.has("benefits")) tabs.push(["benefits", "rwTabBenefits"]);
  const [tab, setTab] = useState(tabs[0]?.[0] ?? "plans");
  return (
    <div className="page">
      {tabs.length > 1 && (
        <div className="tabs" role="tablist">
          {tabs.map(([k, label]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>{t(label)}</button>)}
        </div>
      )}
      {tab === "plans" ? <Plans ws={ws} /> : <Benefits ws={ws} />}
    </div>
  );
}

function Plans({ ws }: { ws: Workspace }) {
  const { t, lang } = useI18n();
  const cid = ws.membership.company_id;
  const manage = ws.permissions.has("payroll.manage");
  const [plans, setPlans] = useState<RewardPlan[] | null>(null);
  const [lines, setLines] = useState<RewardLine[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [depts, setDepts] = useState<Department[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [f, setF] = useState({ title: "", key: "bonus" as RewardKey, month: iso(new Date()).slice(0, 7), notes: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => { setPlans(await loadRewardPlans(cid)); setLines(await loadRewardLines(cid)); };
  useEffect(() => {
    void refresh().catch((e) => setMsg({ ok: false, text: e.message }));
    void loadDirectory(cid).then(setPeople);
    void loadOrganisation(cid).then((o) => setDepts(o.departments));
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const month = (m: string) => new Date(`${m}T00:00:00`).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { month: "long", year: "numeric" });
  const totals = useMemo(() => {
    const m = new Map<string, { n: number; sum: number }>();
    for (const l of lines) { const x = m.get(l.plan_id) ?? { n: 0, sum: 0 }; m.set(l.plan_id, { n: x.n + (Number(l.amount) > 0 ? 1 : 0), sum: x.sum + Number(l.amount) }); }
    return m;
  }, [lines]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { const id = await createRewardPlan(cid, f.title, f.key, `${f.month}-01`, f.notes); setF({ ...f, title: "", notes: "" }); await refresh(); setOpen(id); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  const plan = plans?.find((p) => p.id === open);

  return (
    <>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {plan ? (
        <PlanView plan={plan} lines={lines.filter((l) => l.plan_id === plan.id)} people={people} depts={depts} canManage={manage}
          canApprove={ws.permissions.has("payroll.approve")} onBack={() => setOpen(null)} onChanged={refresh} />
      ) : (
        <>
          <section className="card">
            <h2>{t("rwPlans")}</h2>
            {plans === null ? <p className="muted">{t("loading")}</p> : plans.length === 0 ? <p className="muted">{t("rwNone")}</p> : (
              <div className="table-wrap"><table>
                <thead><tr><th>{t("rwTitle")}</th><th>{t("otKind")}</th><th className="hide-sm">{t("rwPayMonth")}</th><th>{t("payPeople")}</th><th>{t("rwTotal")}</th><th>{t("status")}</th></tr></thead>
                <tbody>{plans.map((p) => (
                  <tr key={p.id}>
                    <td><button type="button" className="link strong" onClick={() => setOpen(p.id)}>{p.title}</button></td>
                    <td>{t(`rwKey_${p.element_key}` as StringKey)}</td>
                    <td className="hide-sm">{month(p.pay_month)}</td>
                    <td className="mono">{totals.get(p.id)?.n ?? 0}</td>
                    <td className="mono">{money(totals.get(p.id)?.sum ?? 0)}</td>
                    <td><span className={`badge ${p.status === "draft" ? "warning" : "success"}`}>{t(`rwStatus_${p.status}` as StringKey)}</span></td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
            <p className="muted small-note">{t("rwHelp")}</p>
          </section>
          {manage && (
            <form className="card" onSubmit={create}>
              <h2>{t("rwNew")}</h2>
              <div className="field-row">
                <label className="field"><span>{t("rwTitle")}</span><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required placeholder={t("rwTitleExample")} /></label>
                <label className="field"><span>{t("otKind")}</span>
                  <select value={f.key} onChange={(e) => setF({ ...f, key: e.target.value as RewardKey })}>
                    {KEYS.map((k) => <option key={k} value={k}>{t(`rwKey_${k}` as StringKey)}</option>)}
                  </select></label>
                <label className="field"><span>{t("rwPayMonth")}</span><input type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} required /></label>
              </div>
              <label className="field"><span>{t("note")}</span><input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
              {f.key === "profit_share" && <p className="muted small-note">{t("rwProfitNote")}</p>}
              <div className="actions"><button className="btn primary">{t("rwCreate")}</button></div>
            </form>
          )}
        </>
      )}
    </>
  );
}

function PlanView({ plan, lines, people, depts, canManage, canApprove, onBack, onChanged }: {
  plan: RewardPlan; lines: RewardLine[]; people: DirectoryRow[]; depts: Department[]; canManage: boolean; canApprove: boolean;
  onBack: () => void; onChanged: () => Promise<void>;
}) {
  const { t, pick } = useI18n();
  const draft = plan.status === "draft";
  const [fill, setFill] = useState({ how: "months", value: "0.5", dept: "" });
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const amount = (emp: string) => lines.find((l) => l.employment_id === emp)?.amount ?? 0;
  const shown = people.filter((p) => p.status !== "terminated" || lines.some((l) => l.employment_id === p.employment_id));
  const total = lines.reduce((a, l) => a + Number(l.amount), 0);

  async function act(fn: () => Promise<unknown>, done: string) {
    setMsg(null);
    try { await fn(); await onChanged(); setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  async function saveEdits() {
    for (const [emp, v] of Object.entries(edits)) await setRewardLine(plan.id, emp, Number(v || 0));
    setEdits({});
  }

  return (
    <>
      <button type="button" className="link back" onClick={onBack}>← {t("rwPlans")}</button>
      <section className="card">
        <div className="row spread">
          <h2>{plan.title} <span className={`badge ${draft ? "warning" : "success"}`}>{t(`rwStatus_${plan.status}` as StringKey)}</span></h2>
          <span className="mono strong">{money(total)}</span>
        </div>
        <p className="muted">{t(`rwKey_${plan.element_key}` as StringKey)} · {plan.pay_month.slice(0, 7)}{plan.notes ? ` · ${plan.notes}` : ""}</p>
        {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
        {draft && canManage && (
          <div className="row">
            <select aria-label={t("rwFillHow")} value={fill.how} onChange={(e) => setFill({ ...fill, how: e.target.value })}>
              <option value="months">{t("rwMonthsOfBasic")}</option><option value="fixed">{t("rwFixed")}</option>
            </select>
            <input type="number" min="0" step="0.01" style={{ width: 110 }} aria-label={t("payAmount")} value={fill.value} onChange={(e) => setFill({ ...fill, value: e.target.value })} />
            <select aria-label={t("department")} value={fill.dept} onChange={(e) => setFill({ ...fill, dept: e.target.value })}>
              <option value="">{t("rwEveryone")}</option>
              {depts.map((d) => <option key={d.id} value={d.id}>{pick(d.name, d.name_ar)}</option>)}
            </select>
            <button type="button" className="btn secondary small" onClick={() => act(() => fillRewardPlan(plan.id, fill.how === "fixed" ? Number(fill.value) : null,
              fill.how === "months" ? Number(fill.value) : null, fill.dept || null), t("rwFilled"))}>{t("rwFill")}</button>
          </div>
        )}
        <div className="row">
          {draft && canManage && Object.keys(edits).length > 0 && <button type="button" className="btn secondary" onClick={() => act(saveEdits, t("saved"))}>{t("save")}</button>}
          {draft && canApprove && <button type="button" className="btn primary" onClick={() => act(() => approveRewardPlan(plan.id), t("rwApproved"))}>{t("rwApprove")}</button>}
          {draft && canManage && <button type="button" className="btn secondary" onClick={async () => { if (window.confirm(t("rwDeleteAsk"))) { await deleteRewardPlan(plan.id); await onChanged(); onBack(); } }}>{t("payDelete")}</button>}
        </div>
      </section>
      <section className="card">
        <div className="table-wrap"><table>
          <thead><tr><th>{t("employee")}</th><th className="hide-sm">{t("department")}</th><th>{t("payAmount")}</th></tr></thead>
          <tbody>{shown.map((p) => (
            <tr key={p.employment_id}>
              <td>{pick(p.full_name, p.full_name_ar)}</td>
              <td className="hide-sm">{pick(p.department_name ?? "", p.department_name_ar)}</td>
              <td>{draft && canManage
                ? <input type="number" min="0" step="0.01" style={{ width: 130 }} aria-label={t("payAmount")}
                    value={edits[p.employment_id] ?? (amount(p.employment_id) ? String(amount(p.employment_id)) : "")}
                    onChange={(e) => setEdits({ ...edits, [p.employment_id]: e.target.value })} />
                : <span className="mono">{amount(p.employment_id) ? money(amount(p.employment_id)) : "—"}</span>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </section>
    </>
  );
}

function Benefits({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const manage = ws.permissions.has("payroll.manage");
  const [plans, setPlans] = useState<BenefitPlan[] | null>(null);
  const [enr, setEnr] = useState<Enrollment[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [f, setF] = useState({ name: "", name_ar: "", category: "medical", provider: "", er: "", ee: "0", dep: "0" });
  const [pick2, setPick2] = useState({ sel: new Set<string>(), from: iso(new Date()), dependents: "0" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const today = iso(new Date());
  const refresh = async () => { setPlans(await loadBenefitPlans(cid)); setEnr(await loadEnrollments(cid)); };
  useEffect(() => { void refresh(); void loadDirectory(cid).then((p) => setPeople(p.filter((x) => x.status !== "terminated"))); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = (planId: string) => enr.filter((e) => e.plan_id === planId && e.start_date <= today && (!e.end_date || e.end_date >= today));
  const cost = (p: BenefitPlan, e: Enrollment) => ({ er: Number(p.employer_cost) * (1 + e.dependents * Number(p.dependent_factor)), ee: Number(p.employee_cost) * (1 + e.dependents * Number(p.dependent_factor)) });
  const monthly = (plans ?? []).reduce((a, p) => current(p.id).reduce((b, e) => ({ er: b.er + cost(p, e).er, ee: b.ee + cost(p, e).ee }), a), { er: 0, ee: 0 });
  const name = (id: string) => { const p = people.find((x) => x.employment_id === id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };

  async function act(fn: () => Promise<unknown>, done: string) {
    setMsg(null);
    try { await fn(); await refresh(); setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const plan = plans?.find((p) => p.id === open);

  return (
    <>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="kpis">
        <div className="stat"><span className="stat-label">{t("bnEmployerMonthly")}</span><span className="stat-value small mono">{money(monthly.er)}</span></div>
        <div className="stat"><span className="stat-label">{t("bnEmployeeMonthly")}</span><span className="stat-value small mono">{money(monthly.ee)}</span></div>
      </div>
      <section className="card">
        <h2>{t("rwTabBenefits")}</h2>
        {plans === null ? <p className="muted">{t("loading")}</p> : plans.length === 0 ? <p className="muted">{t("bnNone")}</p> : (
          <ul className="req-list">
            {plans.map((p) => (
              <li key={p.id}>
                <span><button type="button" className="link strong" onClick={() => setOpen(open === p.id ? null : p.id)}>{pick(p.name_en, p.name_ar)}</button>
                  <span className="muted small-note"> · {t(`bnCat_${p.category}` as StringKey)}{p.provider ? ` · ${p.provider}` : ""} · {t("bnCompanyPays")} {money(p.employer_cost)}{Number(p.employee_cost) ? ` · ${t("bnEmployeePays")} ${money(p.employee_cost)}` : ""}</span></span>
                <span className="badge info">{current(p.id).length} {t("payPeople").toLowerCase()}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="muted small-note">{t("bnHelp")}</p>
      </section>
      {plan && (
        <section className="card">
          <h2>{pick(plan.name_en, plan.name_ar)}</h2>
          <ul className="req-list">
            {current(plan.id).map((e) => (
              <li key={e.id}>
                <span>{name(e.employment_id)}{e.dependents ? <span className="muted small-note"> · {e.dependents} {t("bnDependents")}</span> : null}
                  <span className="muted small-note"> · {t("payFrom")} {e.start_date}</span></span>
                {manage && <button type="button" className="btn secondary small" onClick={() => act(() => endBenefit(e.id, today), t("bnEnded"))}>{t("bnEnd")}</button>}
              </li>
            ))}
          </ul>
          {manage && (
            <form className="stack" onSubmit={(ev) => { ev.preventDefault(); void act(() => enrollBenefit(plan.id, [...pick2.sel], pick2.from, Number(pick2.dependents)), t("bnEnrolled")).then(() => setPick2({ ...pick2, sel: new Set() })); }}>
              <h3 className="sub-head">{t("bnEnroll")}</h3>
              <div className="field-row">
                <label className="field"><span>{t("payFrom")}</span><input type="date" value={pick2.from} onChange={(e) => setPick2({ ...pick2, from: e.target.value })} /></label>
                <label className="field"><span>{t("bnDependents")}</span><input type="number" min="0" max="10" value={pick2.dependents} onChange={(e) => setPick2({ ...pick2, dependents: e.target.value })} /></label>
              </div>
              <div className="pick-list">
                {people.filter((p) => !current(plan.id).some((e) => e.employment_id === p.employment_id)).map((p) => (
                  <label key={p.employment_id} className="check"><input type="checkbox" checked={pick2.sel.has(p.employment_id)}
                    onChange={(e) => { const s = new Set(pick2.sel); if (e.target.checked) s.add(p.employment_id); else s.delete(p.employment_id); setPick2({ ...pick2, sel: s }); }} /> {pick(p.full_name, p.full_name_ar)}</label>
                ))}
              </div>
              <div className="actions"><button className="btn primary" disabled={pick2.sel.size === 0}>{t("bnEnroll")} ({pick2.sel.size})</button></div>
            </form>
          )}
        </section>
      )}
      {manage && (
        <form className="card" onSubmit={(e) => { e.preventDefault(); void act(() => saveBenefitPlan(cid, { name_en: f.name, name_ar: f.name_ar || null, category: f.category, provider: f.provider || null,
          employer_cost: Number(f.er || 0), employee_cost: Number(f.ee || 0), dependent_factor: Number(f.dep || 0) }), t("saved")).then(() => setF({ ...f, name: "", name_ar: "", provider: "" })); }}>
          <h2>{t("bnNew")}</h2>
          <div className="field-row">
            <label className="field"><span>{t("nameEn")}</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></label>
            <label className="field"><span>{t("nameAr")}</span><input dir="rtl" value={f.name_ar} onChange={(e) => setF({ ...f, name_ar: e.target.value })} /></label>
            <label className="field"><span>{t("otKind")}</span>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                {["medical", "life", "transport", "phone", "meal", "wellbeing", "other"].map((c) => <option key={c} value={c}>{t(`bnCat_${c}` as StringKey)}</option>)}
              </select></label>
            <label className="field"><span>{t("bnProvider")}</span><input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} /></label>
          </div>
          <div className="field-row">
            <label className="field"><span>{t("bnCompanyPays")} / {t("bnMonth")}</span><input type="number" min="0" step="0.01" value={f.er} onChange={(e) => setF({ ...f, er: e.target.value })} required /></label>
            <label className="field"><span>{t("bnEmployeePays")} / {t("bnMonth")}</span><input type="number" min="0" step="0.01" value={f.ee} onChange={(e) => setF({ ...f, ee: e.target.value })} /></label>
            <label className="field"><span>{t("bnDepFactor")}</span><input type="number" min="0" step="0.1" value={f.dep} onChange={(e) => setF({ ...f, dep: e.target.value })} /></label>
          </div>
          <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
        </form>
      )}
    </>
  );
}
